'use client'
import LINKS from 'src/_helpers/links'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import { buildJournalArticleUrl } from 'src/_helpers/journals/buildJournalArticleUrl'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'
import Typography from 'src/components/design-system/Typography'
import { JournalArticleItem } from 'src/components/items/JournalArticleItem'
import Button from 'src/components/design-system/Button'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
// icon
import Arrow from 'public/icons/arrows/arrow-right.svg'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
// end icon
const formatDate = (rawDate) => {
  if (!rawDate) return null

  const date = new Date(rawDate)
  if (!Number.isNaN(date.getTime())) {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  if (typeof rawDate === 'string') {
    const normalized = rawDate.replace(/\//g, '.').trim()
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(normalized)) {
      return normalized
    }
  }

  return typeof rawDate === 'string' ? rawDate : null
}

const hexToRgba = (hex, alpha) => {
  if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null

  const value = hex.slice(1)
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export default function AheadOfPrint({ block, locale, variant = 'journal', journal = null }) {
  const articles = Array.isArray(block.articles) ? block.articles : []
  const translate = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const translation = getPreferredTranslation(block, locale) || {}
  const title = translation?.title?.trim()
  const journalTranslation = block.journal ? getPreferredTranslation(block.journal, locale) : {}
  const journalSlug = journalTranslation?.slug
  const isDomainJournal = variant === 'journal-domain'
  const linkAheadOfPrint = buildLocalizedPath(
    isDomainJournal ? journalRoutes.JOURNALS.AHEAD_OF_PRINT : LINKS.JOURNALS.AHEAD_OF_PRINT,
    locale,
    { journal: journalSlug },
  )
  const primaryBackground = hexToRgba(journal?.primaryColor || block?.journal?.primaryColor, 0.1)
  const lineColorClass = isDomainJournal && primaryBackground ? 'border-y-basic-gray8' : 'border-y-dark-blue-dark-blue4'
  const itemLineColorClass =
    isDomainJournal && primaryBackground ? 'after:bg-basic-gray8' : 'after:bg-dark-blue-dark-blue5'
  if (!articles.length) return null

  return (
    <section
      className={`py-64 d:px-40 px-16 ${
        isDomainJournal && primaryBackground ? '' : 'bg-dark-blue-dark-blue6 full-width-shadow-dark-blue-dark-blue6'
      } ${block.type.value}`}
      style={
        isDomainJournal && primaryBackground
          ? {
              backgroundColor: primaryBackground,
              boxShadow: `0 0 0 25vmax ${primaryBackground}`,
              clipPath: 'inset(0 -25vmax)',
            }
          : undefined
      }
      id={`${block.uuid}`}
    >
      {title && (
        <Typography variant="h2-caps-bold" color="text-basic-black" className="pb-32">
          {title}
        </Typography>
      )}

      <ul className={`grid gap-x-42 gap-y-24 t:grid-cols-2 d:grid-cols-4 border-y-1 ${lineColorClass} py-16`}>
        {articles.map((article, i) => {
          const translationA = getPreferredTranslation(article, locale) || {}
          const title = translationA?.title
          const date = formatDate(article?.publishedAt)
          const url =
            buildJournalArticleUrl(article, locale, isDomainJournal ? 'journal-domain' : 'main', journalRoutes) || '#'

          const tags = Array.isArray(translationA?.tags)
            ? translationA.tags.map((label) => {
                const normalized = String(label).toLowerCase()
                const type = normalized.includes('nowość') || normalized.includes('nowosc') ? 'new' : 'default'
                return { label, type }
              })
            : []

          const authors = (article.author || []).map((a) => a?.fullName).filter(Boolean)
          return (
            <li
              key={i}
              className={`relative after:h-full after:w-1 ${itemLineColorClass} after:absolute after:top-0 after:-right-21 after:hidden d:after:block d:last:after:hidden`}
            >
              <JournalArticleItem
                date={date}
                title={title}
                url={url}
                tags={[
                  article.novelty && { label: translate('T_NOVELTY'), type: 'new' },
                  //article.openAccess && { label: translate('T_OPEN_ACCESS'), type: '' },
                  ...tags,
                ]}
                author={authors.join(', ')}
                variant={isDomainJournal && primaryBackground ? 'primary-background' : ''}
              />
            </li>
          )
        })}
      </ul>

      <div></div>
      {linkAheadOfPrint && articles.length > 4 && (
        <Button
          href={linkAheadOfPrint}
          className="w-fit mt-24 d:mt-56 bg-transparent hover:bg-basic-white relative mx-auto block"
          size="l"
        >
          <span className="flex items-center gap-x-8">
            {translate('T_SEE_AHEAD_OF_PRINT')}
            <span className="w-18 h-18 flex items-center justify-center rounded-full border-1 border-dark-blue-dark-blue2">
              <Arrow className="text-dark-blue-dark-blue2" />
            </span>
          </span>
        </Button>
      )}
    </section>
  )
}
